import { useEffect, useRef, useState } from "react";
import { oauth } from "../utils";

interface usePollBackgroundEventProps {
  path: string;
  payload?: {
    [key: string]: any;
  };
  frequency?: number;
  enabled?: boolean;
  pollKey?: number;
}

export type PollStatus = "in_progress" | "successful" | "failed" | "new" | "timed_out" | null;

const responseJsonNormalizer = (json: any) => {
  if (json == null) return null;
  const status = json?.status;
  if (
    status === "in_progress" ||
    status === "successful" ||
    status === "failed" ||
    status === "new" ||
    status === "timed_out"
  ) {
    return status;
  }
  return null;
};

export default function usePollBackgroundEvent({
  path,
  payload,
  frequency = 5000,
  enabled = true,
  pollKey = 0,
}: usePollBackgroundEventProps) {
  const [pollStatus, setPollStatus] = useState<PollStatus>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const clearPollTimer = () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    if (!path || !enabled) {
      clearPollTimer();
      return;
    }

    let pollingEnded = false;

    const scheduleNextPoll = () => {
      clearPollTimer();
      timerRef.current = setTimeout(() => {
        polledEvent();
      }, frequency);
    };

    const polledEvent = async () => {
      if (pollingEnded) return;

      try {
        const { json } = await oauth("GET", path, payload ?? {});
        if (pollingEnded) return;

        const status = responseJsonNormalizer(json);
        if (status === null) {
          setPollStatus(status);
          return;
        }

        if (status === "in_progress" || status === "new") {
          setPollStatus(status);
          scheduleNextPoll();
          return;
        }

        if (status === "successful" || status === "failed" || status === "timed_out") {
          setPollStatus(status);
          return;
        }
      } catch (error) {
        if (pollingEnded) return;
        console.error(`usePollBackgroundEvent failed:`, error);
        setPollStatus(null);
      }
    };

    polledEvent();

    return () => {
      pollingEnded = true;
      clearPollTimer();
    };
  }, [path, payload, frequency, enabled, pollKey]);

  return pollStatus;
}
